import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoiceAPI } from '../api/api';
import { Button } from '../components/ui/button';
import { 
  FileText, 
  Plus, 
  Trash, 
  PencilSimple, 
  Copy,
  CurrencyDollar,
  CalendarBlank,
  CaretRight,
  SpinnerGap
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const InvoiceTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    line_items: [{ description: '', quantity: 1, unit_price: 0, amount: 0 }],
    tax_rate: 0,
    notes: '',
    default_due_days: 30
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await invoiceAPI.getTemplates();
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast({
        title: 'Error',
        description: 'Failed to load templates',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLineItemChange = (index, field, value) => {
    const newLineItems = [...formData.line_items];
    newLineItems[index][field] = field === 'description' ? value : parseFloat(value) || 0;
    if (field === 'quantity' || field === 'unit_price') {
      newLineItems[index].amount = newLineItems[index].quantity * newLineItems[index].unit_price;
    }
    setFormData({ ...formData, line_items: newLineItems });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: '', quantity: 1, unit_price: 0, amount: 0 }]
    });
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length > 1) {
      const newLineItems = formData.line_items.filter((_, i) => i !== index);
      setFormData({ ...formData, line_items: newLineItems });
    }
  };

  const calculateSubtotal = () => {
    return formData.line_items.reduce((sum, item) => sum + item.amount, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await invoiceAPI.updateTemplate(editingTemplate._id, formData);
        toast({ title: 'Success', description: 'Template updated successfully' });
      } else {
        await invoiceAPI.createTemplate(formData);
        toast({ title: 'Success', description: 'Template created successfully' });
      }
      setShowCreateModal(false);
      setEditingTemplate(null);
      resetForm();
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save template',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      line_items: template.line_items,
      tax_rate: template.tax_rate,
      notes: template.notes || '',
      default_due_days: template.default_due_days
    });
    setShowCreateModal(true);
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await invoiceAPI.deleteTemplate(templateId);
      toast({ title: 'Success', description: 'Template deleted successfully' });
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete template',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      line_items: [{ description: '', quantity: 1, unit_price: 0, amount: 0 }],
      tax_rate: 0,
      notes: '',
      default_due_days: 30
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-neutral-900 flex items-center justify-center">
        <SpinnerGap className="h-12 w-12 text-[#2B4C6F] animate-spin" weight="bold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-neutral-900 pb-24 transition-colors duration-300" data-testid="invoice-templates-page">
      {/* Header */}
      <div className="bg-[#2B4C6F] text-white py-6 border-b-4 border-black dark:border-neutral-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-black">Invoice Templates</h1>
              <p className="text-blue-100 mt-1">Save time with reusable invoice templates</p>
            </div>
            <Button
              onClick={() => {
                resetForm();
                setEditingTemplate(null);
                setShowCreateModal(true);
              }}
              className="h-14 px-6 bg-white text-[#2B4C6F] font-bold text-lg rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:translate-y-1 hover:shadow-[2px_2px_0px_#000] transition-all"
              data-testid="create-template-btn"
            >
              <Plus size={24} weight="bold" className="mr-2" />
              New Template
            </Button>
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {templates.length === 0 ? (
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-600 rounded-xl p-12 text-center shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <FileText size={64} weight="bold" className="mx-auto text-gray-400 dark:text-neutral-500 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No Templates Yet</h3>
            <p className="text-gray-600 dark:text-neutral-400 mb-6">Create your first invoice template to speed up billing</p>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="h-12 px-6 bg-[#2B4C6F] text-white font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:translate-y-1 hover:shadow-[1px_1px_0px_#000] transition-all"
            >
              <Plus size={20} weight="bold" className="mr-2" />
              Create Template
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <div
                key={template._id}
                className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-600 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] hover:translate-y-1 hover:shadow-[2px_2px_0px_#000] dark:hover:shadow-[2px_2px_0px_#2B4C6F] transition-all"
                data-testid={`template-card-${template._id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">{template.name}</h3>
                    {template.description && (
                      <p className="text-gray-600 dark:text-neutral-400 text-sm mt-1">{template.description}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(template)}
                      className="p-2 bg-gray-100 dark:bg-neutral-700 rounded-lg hover:bg-gray-200 dark:hover:bg-neutral-600 transition-colors"
                      data-testid={`edit-template-${template._id}`}
                    >
                      <PencilSimple size={20} weight="bold" className="text-gray-600 dark:text-neutral-300" />
                    </button>
                    <button
                      onClick={() => handleDelete(template._id)}
                      className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                      data-testid={`delete-template-${template._id}`}
                    >
                      <Trash size={20} weight="bold" className="text-red-600 dark:text-red-400" />
                    </button>
                  </div>
                </div>

                {/* Template Stats */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-2 text-gray-700 dark:text-neutral-300">
                    <CurrencyDollar size={20} weight="bold" className="text-green-600 dark:text-green-400" />
                    <span className="font-bold text-lg text-green-600 dark:text-green-400">
                      ${template.line_items.reduce((sum, item) => sum + item.amount, 0).toFixed(2)}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-neutral-500">
                      + {template.tax_rate}% tax
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-neutral-400">
                    <CalendarBlank size={20} weight="bold" />
                    <span>Due in {template.default_due_days} days</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-neutral-400">
                    <Copy size={20} weight="bold" />
                    <span>Used {template.use_count || 0} times</span>
                  </div>
                </div>

                {/* Line Items Preview */}
                <div className="bg-gray-50 dark:bg-neutral-700/50 rounded-lg p-3 mb-4">
                  <p className="text-sm font-medium text-gray-700 dark:text-neutral-300 mb-2">
                    {template.line_items.length} line item{template.line_items.length !== 1 ? 's' : ''}
                  </p>
                  {template.line_items.slice(0, 2).map((item, idx) => (
                    <p key={idx} className="text-sm text-gray-600 dark:text-neutral-400 truncate">
                      • {item.description}
                    </p>
                  ))}
                  {template.line_items.length > 2 && (
                    <p className="text-sm text-gray-500 dark:text-neutral-500">
                      +{template.line_items.length - 2} more...
                    </p>
                  )}
                </div>

                {/* Use Template Button */}
                <Button
                  onClick={() => navigate(`/invoices/create?template=${template._id}`)}
                  className="w-full h-12 bg-[#2B4C6F] text-white font-bold rounded-xl border-2 border-black dark:border-neutral-600 shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#2B4C6F] hover:translate-y-1 hover:shadow-[1px_1px_0px_#000] dark:hover:shadow-[1px_1px_0px_#2B4C6F] transition-all"
                  data-testid={`use-template-${template._id}`}
                >
                  Use Template
                  <CaretRight size={20} weight="bold" className="ml-2" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-600 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#2B4C6F]">
            <div className="p-6">
              <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6">
                {editingTemplate ? 'Edit Template' : 'Create Template'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Template Name */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-neutral-300 mb-2">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Weekly Maintenance"
                    required
                    className="w-full h-12 px-4 border-2 border-black dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-gray-900 dark:text-white font-medium focus:ring-2 focus:ring-[#2B4C6F] focus:border-[#2B4C6F] transition-colors"
                    data-testid="template-name-input"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-neutral-300 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this template"
                    className="w-full h-12 px-4 border-2 border-black dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-gray-900 dark:text-white font-medium focus:ring-2 focus:ring-[#2B4C6F] focus:border-[#2B4C6F] transition-colors"
                  />
                </div>

                {/* Line Items */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-neutral-300 mb-2">
                    Line Items
                  </label>
                  <div className="space-y-3">
                    {formData.line_items.map((item, index) => (
                      <div key={index} className="flex gap-2 items-start">
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                          placeholder="Description"
                          required
                          className="flex-1 h-12 px-4 border-2 border-black dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-gray-900 dark:text-white font-medium"
                        />
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                          placeholder="Qty"
                          min="0"
                          step="0.5"
                          required
                          className="w-20 h-12 px-3 border-2 border-black dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-gray-900 dark:text-white font-medium text-center"
                        />
                        <input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)}
                          placeholder="Price"
                          min="0"
                          step="0.01"
                          required
                          className="w-28 h-12 px-3 border-2 border-black dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-gray-900 dark:text-white font-medium"
                        />
                        <div className="w-24 h-12 px-3 border-2 border-gray-300 dark:border-neutral-600 rounded-xl bg-gray-100 dark:bg-neutral-700 flex items-center justify-center text-gray-700 dark:text-neutral-300 font-bold">
                          ${item.amount.toFixed(2)}
                        </div>
                        {formData.line_items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLineItem(index)}
                            className="h-12 w-12 flex items-center justify-center bg-red-100 dark:bg-red-900/30 rounded-xl hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                          >
                            <Trash size={20} weight="bold" className="text-red-600 dark:text-red-400" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    onClick={addLineItem}
                    variant="outline"
                    className="mt-3 h-10 border-2 border-dashed border-gray-400 dark:border-neutral-500 text-gray-600 dark:text-neutral-400 hover:border-[#2B4C6F] hover:text-[#2B4C6F]"
                  >
                    <Plus size={18} weight="bold" className="mr-2" />
                    Add Line Item
                  </Button>
                </div>

                {/* Tax Rate & Due Days */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-neutral-300 mb-2">
                      Tax Rate (%)
                    </label>
                    <input
                      type="number"
                      value={formData.tax_rate}
                      onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                      min="0"
                      max="100"
                      step="0.1"
                      className="w-full h-12 px-4 border-2 border-black dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-gray-900 dark:text-white font-medium"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-neutral-300 mb-2">
                      Default Due Days
                    </label>
                    <input
                      type="number"
                      value={formData.default_due_days}
                      onChange={(e) => setFormData({ ...formData, default_due_days: parseInt(e.target.value) || 30 })}
                      min="1"
                      className="w-full h-12 px-4 border-2 border-black dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-gray-900 dark:text-white font-medium"
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-neutral-300 mb-2">
                    Default Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Notes to include on invoices"
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-black dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-gray-900 dark:text-white font-medium resize-none"
                  />
                </div>

                {/* Totals Preview */}
                <div className="bg-gray-100 dark:bg-neutral-700 rounded-xl p-4">
                  <div className="flex justify-between text-gray-700 dark:text-neutral-300 mb-2">
                    <span>Subtotal:</span>
                    <span className="font-bold">${calculateSubtotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-700 dark:text-neutral-300 mb-2">
                    <span>Tax ({formData.tax_rate}%):</span>
                    <span className="font-bold">${(calculateSubtotal() * formData.tax_rate / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xl font-black text-gray-900 dark:text-white pt-2 border-t border-gray-300 dark:border-neutral-600">
                    <span>Total:</span>
                    <span className="text-green-600 dark:text-green-400">
                      ${(calculateSubtotal() * (1 + formData.tax_rate / 100)).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingTemplate(null);
                      resetForm();
                    }}
                    className="flex-1 h-14 font-bold text-lg border-2 border-black dark:border-neutral-600 rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 h-14 bg-[#2B4C6F] text-white font-bold text-lg rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:translate-y-1 hover:shadow-[2px_2px_0px_#000] transition-all"
                    data-testid="save-template-btn"
                  >
                    {editingTemplate ? 'Update Template' : 'Create Template'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceTemplates;
